import { useState } from 'react'

import { Box, Text, VStack } from '@chakra-ui/react'

import {
  PopoverBody,
  PopoverContent,
  PopoverRoot,
} from '@/components/ui/popover'
import FilterCheckboxOption from '@/components/util-component/FilterCheckboxOption'
import FilterPopoverTrigger from '@/components/util-component/FilterPopoverTrigger'
import { safeJsonParse } from '@/utils/storage'

/** Multiselect "type" facet for the network browser. Selected options
 * combine with OR server-side (see getNetworksWithLastEditor.ts); an
 * empty array means no type filtering. */
export type NetworkTypeFilterOption =
  'marvin' | 'released' | 'regularCopy' | 'main' | 'branches' | 'releaseBranch'

const TYPE_OPTIONS: ReadonlyArray<{
  value: NetworkTypeFilterOption
  label: string
}> = [
  { value: 'marvin', label: 'Marvin' },
  { value: 'released', label: 'Released' },
  { value: 'regularCopy', label: 'Regular copy' },
  { value: 'main', label: 'Main' },
  { value: 'branches', label: 'Branches' },
  { value: 'releaseBranch', label: 'Release branch' },
]

const ALL_TYPES_LABEL = 'All types'

type NetworkTypeFilterProps = {
  value: ReadonlyArray<NetworkTypeFilterOption>
  onChange: (next: ReadonlyArray<NetworkTypeFilterOption>) => void
  disabled?: boolean
  /** Restrict the offered facets. Defaults to all. The Network tree page
   * passes this to drop `main`, which it governs via a dedicated toggle. */
  availableOptions?: ReadonlyArray<NetworkTypeFilterOption>
}

const NetworkTypeFilterDropdown = ({
  value,
  onChange,
  disabled,
  availableOptions,
}: NetworkTypeFilterProps): React.ReactElement => {
  const [open, setOpen] = useState(false)
  const selected = new Set(value)
  const options = availableOptions
    ? TYPE_OPTIONS.filter((o) => availableOptions.includes(o.value))
    : TYPE_OPTIONS

  const handleToggle = (option: NetworkTypeFilterOption): void => {
    const next = new Set(selected)
    if (next.has(option)) {
      next.delete(option)
    } else {
      next.add(option)
    }
    onChange(Array.from(next))
  }

  const handleClear = (): void => onChange([])

  const triggerLabel = describeTypeFilter(value)

  return (
    <PopoverRoot
      open={open}
      positioning={{ placement: 'bottom-start' }}
      onOpenChange={(d) => setOpen(d.open)}
    >
      <FilterPopoverTrigger
        active={selected.size > 0}
        ariaLabel="Filter networks by type"
        disabled={disabled}
        label={triggerLabel}
      />
      <PopoverContent width="220px">
        <PopoverBody p={1}>
          <VStack align="stretch" gap={0}>
            <FilterCheckboxOption
              checked={selected.size === 0}
              label={ALL_TYPES_LABEL}
              onSelect={handleClear}
            />
            <Box borderTopWidth="1px" mt={1} pt={1} px={2} py={1}>
              <Text color="gray.500" fontSize="xs" fontWeight="medium">
                Filter by type
              </Text>
            </Box>
            {options.map((option) => (
              <FilterCheckboxOption
                key={option.value}
                checked={selected.has(option.value)}
                label={option.label}
                onSelect={() => handleToggle(option.value)}
              />
            ))}
          </VStack>
        </PopoverBody>
      </PopoverContent>
    </PopoverRoot>
  )
}

export default NetworkTypeFilterDropdown

export function describeTypeFilter(
  value: ReadonlyArray<NetworkTypeFilterOption>,
): string {
  if (value.length === 0) return ALL_TYPES_LABEL
  if (value.length === 1) {
    return (
      TYPE_OPTIONS.find((o) => o.value === value[0])?.label ?? ALL_TYPES_LABEL
    )
  }
  return `${value.length} types`
}

/** Defensive parser for a stored type filter selection. */
export function parseStoredTypeFilter(
  raw: string | null | undefined,
): ReadonlyArray<NetworkTypeFilterOption> | undefined {
  const parsed = safeJsonParse(raw)
  if (!Array.isArray(parsed)) return undefined
  const validValues = new Set<string>(TYPE_OPTIONS.map((o) => o.value))
  const filtered = parsed.filter(
    (v): v is NetworkTypeFilterOption =>
      typeof v === 'string' && validValues.has(v),
  )
  return filtered
}
